import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useLocalStorage } from '@vueuse/core'
import { APP_ROUTES, QUERY_CACHE_TIME, STORAGE_KEYS } from '~/constants'
import { QUERY_KEYS } from '~/queries/keys'
import { GetCurrentProfileGqlQuery } from '~/queries/misc/get-current-profile.query'
import { useQueryEvents } from '~/composables/useQueryEvents'
import { type GetCurrentProfileQuery, OrganisationGroup } from '~/types/gql/generated/graphql'
import ProfileSwitchModal from '~/components/overlays/ProfileSwitchModal.vue'

export const useProfilerStore = defineStore('profiler', () => {
  const queryClient = useQueryClient()
  const { requester } = useRequester()
  const router = useRouter()
  const sessionStore = useSessionStore()
  const { currentUser, isAuthenticated, isAdmin } = storeToRefs(sessionStore)
  const overlay = useOverlay()
  const profileSwitchModal = overlay.create(ProfileSwitchModal)

  const persistedPreferredProfileId = useLocalStorage(STORAGE_KEYS.PREFERRED_PROFILE_ID, '')

  const isSwitching = ref(false)

  watch(isSwitching, (val) => {
    if (val) {
      profileSwitchModal.open(ProfileSwitchModal)
    }
    else {
      profileSwitchModal.close(ProfileSwitchModal)
    }
  })

  const { t } = useI18n()
  const currentProfileQuery = useQuery({
    refetchInterval: QUERY_CACHE_TIME,
    queryKey: [QUERY_KEYS.profile],
    queryFn: async () => requester(GetCurrentProfileGqlQuery, { id: currentUser.value?.id }),
    enabled: isAuthenticated,
  })
  const {
    data: currentProfileQueryData,
    isFetchedAfterMount,
    isFetching,
    isLoading: isLoadingProfile,
  } = currentProfileQuery

  useQueryEvents<GetCurrentProfileQuery>(currentProfileQuery, {
    onSuccess(data) {
      if (!data?.profile?.currentOrganisation) {
        console.error('No organisation found for the user', data?.profile)

        throw createError({
          status: 418,
          statusText: t('profile-switcher.error-screen.no-organisation.title'),
          message: t('profile-switcher.error-screen.no-organisation.description'),
          fatal: true,
        })
      }
    },
    onError() {
      throw createError({
        status: 418,
        statusText: t('profile-switcher.error-screen.generic.title'),
        message: t('profile-switcher.error-screen.generic.description'),
        fatal: true,
      })
    },
  })

  const currentProfile = computed(() => {
    if (isSwitching.value)
      return null

    return currentProfileQueryData.value?.profile?.currentOrganisation || null
  })

  async function setProfileId(id: number, onBackground = false) {
    if (!onBackground)
      isSwitching.value = true

    persistedPreferredProfileId.value = String(id)
    await sessionStore.regenerateTokenAsync(id)
    // not really great way but most of our requireHard switch with redirectTo will be with the organisationId params
    // must find another way when needs for other page increase, but no faster solution for now
    await invalidateProfile({ organisationId: id })
  }

  /**
   * Sounds a bit of a duplicate, but it is used for the profile switcher.
   * It allows to bind it as v-model
   */
  const currentProfileId = computed<number>({
    get() {
      return currentUser.value?.currentOrganisationId
    },
    set(id: number) {
      return setProfileId(id)
    },
  })

  const allProfilesIds = computed(() => {
    return currentProfileQueryData.value?.profile?.allOrganisations?.map(x => x.id) || []
  })

  const isLoading = computed(() => [isSwitching.value, isLoadingProfile.value, isFetching.value].some(Boolean))

  watch(isFetchedAfterMount, () => {
    isSwitching.value = false
  })

  const profileRouteParams = computed(() => ({
    organisationId: String(currentProfileId.value) || '-',
  }))

  async function invalidateProfile(redirectParams = {}) {
    // organisation specific routes with specific data (eg: /portfolio, deal dashboards,...)
    const requiredHardSwitch = !!router.currentRoute.value.meta.requiresHardSwitch
    let redirectTo = router.currentRoute.value.meta.redirectTo

    const _currentProfile = currentProfileQueryData.value?.profile?.currentOrganisation
    const hasLimitedAppAccess
      = !_currentProfile?.groups?.some(group =>
        [OrganisationGroup.Client, OrganisationGroup.Agent, OrganisationGroup.Investor].includes(group),
      ) && !isAdmin.value

    if (hasLimitedAppAccess) {
      console.debug('Limited view account, redirecting to profile', {
        redirectTo,
        currentProfileId: _currentProfile?.id,
      })
      redirectTo = {
        name: APP_ROUTES.account.name,
        path: APP_ROUTES.account.path,
      }
      Object.assign(redirectParams, {
        organisationId: _currentProfile?.id,
      })

      await router.push({
        ...redirectTo,
        params: redirectParams,
      })
    }
    else if (requiredHardSwitch) {
      if (redirectTo) {
        await router.push({
          ...redirectTo,
          params: redirectParams,
        })
      }
      else {
        // force redirect to ensure correct permissions/access to data
        await router.push(APP_ROUTES.home.path)
      }
    }
    else {
      // force redirect to ensure correct permissions/access to data
      await router.push(APP_ROUTES.home.path)
    }

    await nextTick()
    await queryClient.invalidateQueries()
    // ui smooth delay
    setTimeout(() => {
      isSwitching.value = false
      return Promise.resolve()
    }, 500)
  }

  return {
    profileRouteParams,
    isLoading,
    isSwitching,
    currentProfile,
    currentProfileId,
    allProfilesIds,
    persistedPreferredProfileId,
    setProfileId,
  }
})
